import React, {useCallback, useEffect, useState} from "react";
import AccountVerificationV2_1 from "./layout_1";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {globalBrandConfigState, globalBreadCrumbState} from "../../../_common/global_state";
import {useDeepLinkParams} from "../../../_common/useDeepLinkParams";
import {useGetUserVerificationHistories, useGetUserVerificationProfile} from "../function/accountVerification";
import {Spin} from "antd";
import {
    useGetUserVerificationAccessRightsList,
    useGetUserVerificationFlows,
    useGetUserVerificationTiers
} from "../function/verificationSettings";
import {verificationRefetchState} from "../state";
import AccountVerificationV2_2 from "./layout_2";
import AccountVerificationV2_3 from "./layout_3";
import VerificationLoadError from "./loadError";
import {preloadFailed} from "../lib/load";

export default function AccountVerificationV2() {
    const brandConfigState = useRecoilValue(globalBrandConfigState);
    const { params: deepLinkParams, clearDeepLink } = useDeepLinkParams();
    const [deepLinkTab] = useState(deepLinkParams.tab);
    const [deepLinkId] = useState(deepLinkParams.id);
    const setGlobalBreadCrumbState = useSetRecoilState(globalBreadCrumbState);
    const setVerificationRefetch = useSetRecoilState(verificationRefetchState);

    const {
        data: userVerificationHistories,
        getUserVerificationHistories,
        refetchUserVerificationHistories
    } = useGetUserVerificationHistories();
    const {
        data: userVerificationFlows,
        getUserVerificationFlows,
        refetchUserVerificationFlows
    } = useGetUserVerificationFlows();
    const {
        data: userVerificationTiers,
        getUserVerificationTiers,
        refetchUserVerificationTiers
    } = useGetUserVerificationTiers();
    const {
        data: userVerificationProfile,
        getUserVerificationProfile,
        refetchUserVerificationProfile
    } = useGetUserVerificationProfile();
    const {data: accessRightsList, getUserVerificationAccessRightsList} = useGetUserVerificationAccessRightsList()

    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    const preload = useCallback(async () => {
        setIsLoading(true);

        // allSettled, not all: one failed call must not hide the others' errors,
        // and the steps must not render on half the data (see lib/load.js).
        const [accessRights, ...required] = await Promise.allSettled([
            getUserVerificationAccessRightsList(),
            getUserVerificationFlows(),
            getUserVerificationProfile(),
            getUserVerificationTiers({
                populate: ["documentSettings"]
            }),
            getUserVerificationHistories(),
        ]);

        // Access rights only label the "Features Unlocked" list; the steps work without them.
        if (accessRights.status === "rejected") console.error(accessRights.reason);

        setLoadFailed(preloadFailed(required));
        setIsLoading(false);
    }, [getUserVerificationFlows, getUserVerificationProfile, getUserVerificationTiers, getUserVerificationHistories, getUserVerificationAccessRightsList])

    useEffect(() => {
        preload();
    }, [preload])

    useEffect(() => {
        if (deepLinkTab || deepLinkId) {
            clearDeepLink();
        }
        setGlobalBreadCrumbState([
            {
                name: "Home",
                path: "/dashboard",
            },
            {
                name: "Verification",
                path: "/account-verification",
            },
        ]);
    }, []);

    useEffect(() => {
        setVerificationRefetch({
            refetchUserVerificationProfile,
            refetchUserVerificationFlows,
            refetchUserVerificationTiers,
            refetchUserVerificationHistories,
        });
    }, [
        refetchUserVerificationProfile,
        refetchUserVerificationFlows,
        refetchUserVerificationTiers,
        refetchUserVerificationHistories,
        setVerificationRefetch
    ]);

    if (isLoading) {
        return (
            <div style={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px"}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (loadFailed) {
        return <VerificationLoadError onRetry={preload}/>;
    }

    let component;

    const props = {
        userVerificationFlows,
        getUserVerificationFlows,
        userVerificationProfile,
        refetchUserVerificationProfile,
        userVerificationTiers,
        refetchUserVerificationTiers,
        getUserVerificationTiers,
        userVerificationHistories,
        refetchUserVerificationHistories,
        accessRightsList,
        deepLinkTab,
        deepLinkId,
    }

    switch (brandConfigState?.defaultLayout) {
        case 1:
            component = <AccountVerificationV2_1{...props}/>
            break;

        case 2:
            component = <AccountVerificationV2_2{...props}/>
            break;

        case 3:
            component = <AccountVerificationV2_3{...props}/>
            break;

        default:
            return null
    }


    return component;
}